/**
 * Floating Finance Partner Eligibility panel. Portaled to `.app` like Setback Notes
 * so iPad iframe overflow cannot swallow the toggle.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Tooltip } from 'antd';
import {
    ArrowsLeftRightIcon,
    BankIcon,
    CaretDownIcon,
    CaretRightIcon,
    CheckCircleIcon,
    InfoIcon,
    WarningCircleIcon,
    XCircleIcon,
} from '@phosphor-icons/react';
import { MdClose } from 'react-icons/md';
import { useViewerStore } from '../../../../store/store';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { useCurrentUser } from '../../../../contexts/UserContext';
import { resolveEnergyCommunityLikelyQualifies } from '../Three-JS-Viewer/EnergyCommunityNotice';
import { resolveEffectiveAnnualUsageKwh } from '../../../../helpers/utilityMonthlyDailyToAnnual';
import { getOpportunityPrimaryFinancePartner } from '../oneBUTTONDashboard/contactCreditStatus';
import { SETBACK_NOTES_OPENED_EVENT } from '../Setback Notes/Note';
import {
    collectFinanceEligibilityCurrentValues,
    type FinanceEligibilitySceneValues,
} from '../Three-JS-Viewer/Controllers/FinanceEligibilityControl';
import FinancePartnerChangeModal from './FinancePartnerChangeModal';
import {
    EligibilityDetailsContext,
    EligibilityDetailsModal,
    type EligibilityDetails,
} from './EligibilityDetailsPopup';
import {
    ELIGIBILITY_ROWS,
    INSTALLATION_TYPE_ROWS,
    PROPERTY_TYPE_ROWS,
    SYSTEM_SPEC_ROWS,
    evaluateFinanceEligibility,
    fetchFinanceEligibilityCriteria,
    fetchFinanceEligibilityPropertyDetails,
    findFinanceEligibilityPartner,
    formatEligibilityCurrentValue,
    getEligibilityRowInfo,
    getOverallEligibilityReasonRows,
    getOverallEligibilityStatus,
    isCurrentNestedEligibilityRow,
    isFinanceEligibilityValuePartner,
    getSuggestedFinanceEligibilityPartner,
    type EligibilityCurrentValues,
    type EligibilityReasonRow,
    type EligibilityStatus,
    type FinanceEligibilityCatalog,
    type FinanceEligibilityPartner,
} from '../../../../services/financeEligibilityService';

function opportunityHasBattery(
    opportunityDetails: any,
    productType?: string,
    financeProductType?: string,
): boolean {
    const codes = `${productType ?? ''}${financeProductType ?? ''}`.toUpperCase();
    if (codes.includes('B')) return true;
    const interest = opportunityDetails?.OpportunityInterest ?? opportunityDetails?.opportunityInterest;
    if (interest?.Battery || interest?.BatteryOnly) return true;
    const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    return Array.isArray(products)
        && products.some((product: any) => /battery/i.test(String(product?.Product_Type_Name ?? product?.product_Type_Name ?? '')));
}

function StatusIcon({
    status,
    rowKey,
    partner,
    values,
    reason: reasonOverride,
    reasonRows,
    label,
}: {
    status: EligibilityStatus;
    rowKey?: string;
    partner: FinanceEligibilityPartner | null;
    values: EligibilityCurrentValues;
    reason?: string;
    reasonRows?: EligibilityReasonRow[];
    label?: string;
}) {
    const reason =
        reasonOverride ??
        (rowKey && (status === 'failed' || status === 'warning')
            ? getEligibilityRowInfo(rowKey, partner, values, status).reason
            : '');
    const icon =
        status === 'success' ? (
            <CheckCircleIcon
                size={20}
                weight="fill"
                className="fp-eligibility__icon fp-eligibility__icon--success"
                aria-label="Success"
            />
        ) : status === 'failed' ? (
            <XCircleIcon
                size={20}
                weight="fill"
                className="fp-eligibility__icon fp-eligibility__icon--failed"
                aria-label="Failed"
            />
        ) : (
            <WarningCircleIcon
                size={20}
                weight="fill"
                className="fp-eligibility__icon fp-eligibility__icon--warning"
                aria-label="Warning"
            />
        );
    const showDetails = React.useContext(EligibilityDetailsContext);
    const heading = status === 'failed' ? 'Not eligible' : 'Review';
    const hasDetails = Boolean(reason || reasonRows?.length);
    const content = (
        <span
            className={`fp-eligibility__icon-wrap${hasDetails ? ' fp-eligibility__icon-wrap--help' : ''}`}
            role={hasDetails && showDetails ? 'button' : undefined}
            tabIndex={hasDetails && showDetails ? 0 : undefined}
            onClick={(event) => {
                event.stopPropagation();
                if (hasDetails && showDetails) showDetails({ heading, rows: reasonRows, reason });
            }}
            onKeyDown={(event) => {
                if (!hasDetails || !showDetails) return;
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                event.stopPropagation();
                showDetails({ heading, rows: reasonRows, reason });
            }}
            onMouseDown={(event) => event.stopPropagation()}
        >
            {icon}
            {label ? (
                <span className={`fp-eligibility__overall-label fp-eligibility__overall-label--${status}`}>{label}</span>
            ) : null}
        </span>
    );

    if (!hasDetails) {
        return label ? content : icon;
    }

    return (
        <Tooltip
            title={reasonRows?.length ? `${heading} — click for details` : reason}
            overlayClassName="fp-eligibility-tooltip"
            placement="left"
            mouseEnterDelay={0.15}
            zIndex={11000}
        >
            {content}
        </Tooltip>
    );
}

function EligibilityInfoButton({
    label,
    rowKey,
    partner,
    values,
}: {
    label: string;
    rowKey: string;
    partner: FinanceEligibilityPartner | null;
    values: EligibilityCurrentValues;
}) {
    const info = getEligibilityRowInfo(rowKey, partner, values, 'success');
    return (
        <Tooltip
            title={
                <div className="fp-eligibility__info-tooltip">
                    <div>
                        <div className="fp-eligibility__info-heading">Rule</div>
                        <div>{info.requirement}</div>
                    </div>
                    {info.notes ? (
                        <div>
                            <div className="fp-eligibility__info-heading">Notes</div>
                            <div className="fp-eligibility__info-notes">{info.notes}</div>
                        </div>
                    ) : null}
                </div>
            }
            overlayClassName="fp-eligibility-tooltip"
            placement="right"
            trigger={['hover', 'click']}
            mouseEnterDelay={0.15}
            zIndex={11000}
        >
            <button
                type="button"
                className="fp-eligibility__info-btn"
                aria-label={`${label} requirement for the active finance partner`}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <InfoIcon size={14} weight="bold" />
            </button>
        </Tooltip>
    );
}

/**
 * Design numbers supplied by hosts that run oneDRAW in an iframe (pre-contract),
 * where this window's viewer store stays empty. Non-positive values fall back to the store.
 */
export type FinanceEligibilityMetricsOverride = {
    multiplier?: number | null;
    totalSystemSize?: number | null;
    moduleCount?: number | null;
    actualWattage?: number | null;
    utilityWattage?: number | null;
    annualUsageKwh?: number | null;
    smallestArrayCount?: number | null;
};

export type FinancePartnerEligibilitySummary = {
    partnerId: string;
    partnerName: string;
    aliases: string[];
    status: EligibilityStatus;
    eligibleCount: number;
    totalCount: number;
    recommended: boolean;
};

type FinancePartnerEligibilityProps = {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    hideFloatingToggle?: boolean;
    metricsOverride?: FinanceEligibilityMetricsOverride | null;
    onPartnerEligibilityChange?: (summaries: FinancePartnerEligibilitySummary[]) => void;
    /** Scene reads (tilt, racking, installation) from a oneDRAW instance in another window. */
    sceneValues?: FinanceEligibilitySceneValues | null;
    /** `modal` centers the panel over a backdrop instead of docking it in the corner. */
    presentation?: 'note' | 'modal';
};

function positiveOverride(value: number | null | undefined): number | null {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function hasText(value: string | null | undefined): boolean {
    return typeof value === 'string' && value.trim() !== '';
}

/** Scene reads win only where this window's viewer has nothing to show. */
function mergeSceneValues(
    values: EligibilityCurrentValues,
    scene: FinanceEligibilitySceneValues | null | undefined,
): EligibilityCurrentValues {
    if (!scene) return values;
    return {
        ...values,
        multiplier: positiveOverride(values.multiplier) ?? scene.multiplier,
        totalSystemSize: positiveOverride(values.totalSystemSize) ?? scene.totalSystemSize,
        moduleCount: values.moduleCount > 0 ? values.moduleCount : scene.moduleCount,
        tiltRange: hasText(values.tiltRange) ? values.tiltRange : scene.tiltRange,
        tiltMin: values.tiltMin ?? scene.tiltMin,
        tiltMax: values.tiltMax ?? scene.tiltMax,
        tiltRoofs: values.tiltRoofs?.length ? values.tiltRoofs : scene.tiltRoofs,
        minPanelsPerArray: hasText(values.minPanelsPerArray)
            ? values.minPanelsPerArray
            : scene.minPanelsPerArray,
        smallestArrayCount: values.smallestArrayCount ?? scene.smallestArrayCount,
        hasBallastedRacking: values.hasBallastedRacking || scene.hasBallastedRacking,
        installationLabel: hasText(values.installationLabel)
            ? values.installationLabel
            : scene.installationLabel,
        installationKeys: values.installationKeys.length
            ? values.installationKeys
            : scene.installationKeys,
        installationPresence: values.installationKeys.length
            ? values.installationPresence
            : scene.installationPresence,
        actualOffsetPercent: hasText(values.actualOffsetPercent)
            ? values.actualOffsetPercent
            : scene.actualOffsetPercent,
        utilityOffsetPercent: hasText(values.utilityOffsetPercent)
            ? values.utilityOffsetPercent
            : scene.utilityOffsetPercent,
    };
}

const FinancePartnerEligibility: React.FC<FinancePartnerEligibilityProps> = ({
    open,
    onOpenChange,
    hideFloatingToggle = false,
    metricsOverride = null,
    onPartnerEligibilityChange,
    sceneValues = null,
    presentation = 'note',
}) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const expand = open ?? uncontrolledOpen;
    const setExpand = (next: boolean) => {
        if (open === undefined) setUncontrolledOpen(next);
        onOpenChange?.(next);
    };
    const [changeOpen, setChangeOpen] = useState(false);
    const [details, setDetails] = useState<EligibilityDetails | null>(null);
    const [propertyTypeOpen, setPropertyTypeOpen] = useState(false);
    const [installationTypeOpen, setInstallationTypeOpen] = useState(false);
    const [systemSpecsOpen, setSystemSpecsOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const [portalTarget, setPortalTarget] = useState<Element | null>(null);
    const [eligibilityCatalog, setEligibilityCatalog] = useState<FinanceEligibilityCatalog>({
        partners: [],
    });
    const [propertyDetails, setPropertyDetails] = useState({ propertyType: '', ownershipType: '' });

    const { hasCapability } = useCurrentUser();
    const financePartnerDetails = useViewerStore((state) => state.financePartnerDetails);
    const opportunityDetails = useOneButtonStore((s: any) => s.opportunityDetails);
    const energyCommunityStatus = useOneButtonStore((s) => s.energyCommunityStatus);
    const salesForceId = useOneButtonStore((s: any) => s.salesForceId);
    const solarUtilityData = useOneButtonStore((s: any) => s.solarUtilityData);
    const pathname = useViewerStore((state) => state.pathname);
    const multiplier = useViewerStore((state) => state.multiplier);
    const totalSystemSize = useViewerStore((state) => state.totalSystemSize);
    const actualWattage = useViewerStore((state) => state.actualWattage);
    const utilityWattage = useViewerStore((state) => state.utilityWattage);
    const SFannualUsage = useViewerStore((state) => state.SFannualUsage);
    const oneButtonAnnualUsageOverride = useViewerStore((state) => state.annualUsage);
    const sceneViewer = useViewerStore((state) => state.sceneViewer);
    const roofInfoArr = useViewerStore((state) => state.roofInfoArr);
    const roofPitchOverrideArr = useViewerStore((state) => state.roofPitchOverrideArr);
    const modeCount = useViewerStore((state) => state.modeCount);
    const productType = useViewerStore((state) => state.productType);
    const detachedStructureBuildingIds = useViewerStore((state) => state.detachedStructureBuildingIds);
    const setbackNotesVisible = pathname === '/configure/modules';
    const useOneButtonAnnualInput = hasCapability('Show_OneButton');
    const annualUsageKwh = useMemo(() => {
        const overrideUsage = positiveOverride(metricsOverride?.annualUsageKwh);
        if (overrideUsage != null) return overrideUsage;
        const su = solarUtilityData?.SolarUtility as Record<string, unknown> | undefined;
        return resolveEffectiveAnnualUsageKwh({
            useOneButtonAnnualInput,
            oneButtonAnnualUsageOverride,
            solarUtility: su,
            sfAnnualUsage: SFannualUsage,
        });
    }, [
        metricsOverride?.annualUsageKwh,
        useOneButtonAnnualInput,
        oneButtonAnnualUsageOverride,
        solarUtilityData,
        SFannualUsage,
    ]);
    const isEnergyCommunity = useMemo(
        () => resolveEnergyCommunityLikelyQualifies(opportunityDetails, energyCommunityStatus),
        [opportunityDetails, energyCommunityStatus],
    );
    const hasBattery = useMemo(
        () => opportunityHasBattery(opportunityDetails, productType, financePartnerDetails?.productType),
        [opportunityDetails, productType, financePartnerDetails?.productType],
    );
    const currentValues = useMemo(() => {
        const values = collectFinanceEligibilityCurrentValues({
            sceneViewer: sceneViewer || {},
            roofInfoArr,
            roofPitchOverrideArr,
            multiplier: positiveOverride(metricsOverride?.multiplier) ?? multiplier,
            totalSystemSize: positiveOverride(metricsOverride?.totalSystemSize) ?? totalSystemSize,
            actualWattage: positiveOverride(metricsOverride?.actualWattage) ?? actualWattage,
            utilityWattage: positiveOverride(metricsOverride?.utilityWattage) ?? utilityWattage,
            annualUsageKwh,
            moduleCount:
                positiveOverride(metricsOverride?.moduleCount) ?? (Number(modeCount) || 0),
            hasBattery,
            detachedStructureBuildingIds,
            isEnergyCommunity,
            propertyType: propertyDetails.propertyType,
            ownershipType: propertyDetails.ownershipType,
        });

        const smallestArrayCount = positiveOverride(metricsOverride?.smallestArrayCount);
        const withSmallest =
            smallestArrayCount == null || values.smallestArrayCount != null
                ? values
                : {
                      ...values,
                      smallestArrayCount,
                      minPanelsPerArray: String(smallestArrayCount),
                  };

        return mergeSceneValues(withSmallest, sceneValues);
    }, [
        sceneViewer,
        roofInfoArr,
        roofPitchOverrideArr,
        metricsOverride,
        sceneValues,
        multiplier,
        totalSystemSize,
        actualWattage,
        utilityWattage,
        annualUsageKwh,
        modeCount,
        hasBattery,
        detachedStructureBuildingIds,
        isEnergyCommunity,
        propertyDetails,
    ]);

    const partnerName =
        financePartnerDetails?.name?.trim() ||
        getOpportunityPrimaryFinancePartner(opportunityDetails).trim() ||
        'Not selected';

    const partner = useMemo(
        () => findFinanceEligibilityPartner(partnerName, eligibilityCatalog),
        [partnerName, eligibilityCatalog],
    );

    const statusByKey = useMemo(
        () => evaluateFinanceEligibility(partnerName, currentValues, eligibilityCatalog),
        [partnerName, currentValues, eligibilityCatalog],
    );
    const hasEligibilityValues = isFinanceEligibilityValuePartner(partner);
    const suggestedPartner = useMemo(
        () => getSuggestedFinanceEligibilityPartner(eligibilityCatalog, currentValues),
        [eligibilityCatalog, currentValues],
    );
    const overallStatus = useMemo(() => getOverallEligibilityStatus(statusByKey), [statusByKey]);
    const overallReasonRows = useMemo(
        () =>
            !hasEligibilityValues || overallStatus === 'success'
                ? []
                : getOverallEligibilityReasonRows(partner, currentValues, statusByKey),
        [hasEligibilityValues, overallStatus, partner, currentValues, statusByKey],
    );
    const overallLabel = !hasEligibilityValues
        ? ''
        : overallStatus === 'success'
          ? 'Eligible'
          : overallStatus === 'failed'
            ? 'Not eligible'
            : 'Review';
    const passedCount = ELIGIBILITY_ROWS.filter((row) => statusByKey[row.key] === 'success').length;
    const criteriaTotal = ELIGIBILITY_ROWS.length;
    const passRatio = criteriaTotal > 0 ? passedCount / criteriaTotal : 0;
    const partnerInitials = partnerName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('') || '—';
    const suggestedName = suggestedPartner?.displayName?.trim() ?? '';
    const showSuggested =
        Boolean(suggestedName) && suggestedName.toLowerCase() !== partnerName.toLowerCase();
    const ringRadius = 20;
    const ringCircumference = 2 * Math.PI * ringRadius;
    const ringOffset = ringCircumference * (1 - (hasEligibilityValues ? passRatio : 0));

    const partnerEligibilitySummaries = useMemo<FinancePartnerEligibilitySummary[]>(() => {
        const statusRank: Record<EligibilityStatus, number> = {
            success: 0,
            warning: 1,
            failed: 2,
        };
        const candidates = (eligibilityCatalog.partners ?? [])
            .filter((candidate) => isFinanceEligibilityValuePartner(candidate))
            .map((candidate) => {
                const statuses = evaluateFinanceEligibility(
                    candidate.displayName,
                    currentValues,
                    eligibilityCatalog,
                );
                return {
                    partnerId: candidate.id,
                    partnerName: candidate.displayName,
                    aliases: candidate.aliases ?? [],
                    status: getOverallEligibilityStatus(statuses),
                    eligibleCount: ELIGIBILITY_ROWS.filter(
                        (row) => statuses[row.key] === 'success',
                    ).length,
                    totalCount: ELIGIBILITY_ROWS.length,
                    energyCommunityWeight: statuses.energyCommunity === 'success' ? 1 : 0,
                };
            })
            .sort(
                (a, b) =>
                    b.eligibleCount - a.eligibleCount ||
                    b.energyCommunityWeight - a.energyCommunityWeight ||
                    statusRank[a.status] - statusRank[b.status],
            );
        const recommendedId =
            candidates.find((candidate) => candidate.status !== 'failed' && candidate.eligibleCount > 0)
                ?.partnerId ?? '';
        return candidates.map(({ energyCommunityWeight: _weight, ...candidate }) => ({
            ...candidate,
            recommended: candidate.partnerId === recommendedId,
        }));
    }, [currentValues, eligibilityCatalog]);

    useEffect(() => {
        onPartnerEligibilityChange?.(partnerEligibilitySummaries);
    }, [onPartnerEligibilityChange, partnerEligibilitySummaries]);

    const handleExpand = () => setExpand(!expand);

    useEffect(() => {
        if (!expand) setChangeOpen(false);
    }, [expand]);

    useEffect(() => {
        setPortalTarget(document.querySelector('.app') ?? document.body);
    }, []);

    useEffect(() => {
        let cancelled = false;
        // const partnerFilter = partnerName && partnerName !== 'Not selected' ? partnerName : undefined;
        fetchFinanceEligibilityCriteria().then((catalog) => {
            if (!cancelled) setEligibilityCatalog(catalog);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        fetchFinanceEligibilityPropertyDetails(salesForceId).then((details) => {
            if (!cancelled) setPropertyDetails(details);
        });
        return () => {
            cancelled = true;
        };
    }, [salesForceId]);

    useEffect(() => {
        const closePanel = () => {
            setExpand(false);
            setPropertyTypeOpen(false);
            setInstallationTypeOpen(false);
            setSystemSpecsOpen(false);
        };
        window.addEventListener(SETBACK_NOTES_OPENED_EVENT, closePanel);
        return () => window.removeEventListener(SETBACK_NOTES_OPENED_EVENT, closePanel);
    }, []);

    useEffect(() => {
        if (!hideFloatingToggle || !expand) return undefined;
        const onDocClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            if (panelRef.current?.contains(target)) return;
            if (target.closest('.fp-eligibility-change-modal, .fp-eligibility-details, .ant-modal-root, .ant-tooltip, .pci-unified-nav__btn--finance-eligibility')) {
                return;
            }
            setExpand(false);
        };
        const timer = window.setTimeout(() => {
            document.addEventListener('click', onDocClick, true);
        }, 0);
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener('click', onDocClick, true);
        };
    }, [hideFloatingToggle, expand]);

    useEffect(() => {
        if (!expand) setDetails(null);
    }, [expand]);

    const asModal = presentation === 'modal';

    const noteNode = (
        <div
            ref={panelRef}
            className={
                asModal
                    ? `note note--finance-eligibility fp-eligibility-modal${
                          propertyTypeOpen || installationTypeOpen || systemSpecsOpen
                              ? ' note--finance-eligibility--specs-open'
                              : ''
                      } ${expand ? 'expand' : 'collapse'}`
                    : `note note__area note--finance-eligibility${hideFloatingToggle ? ' note--finance-eligibility--navbar' : ''}${setbackNotesVisible ? ' note--finance-eligibility--stacked' : ''}${propertyTypeOpen || installationTypeOpen || systemSpecsOpen ? ' note--finance-eligibility--specs-open' : ''} ${expand ? 'expand' : 'collapse'}`
            }
            role={asModal ? 'dialog' : undefined}
            aria-modal={asModal ? true : undefined}
            aria-label={asModal ? 'Finance Partner Eligibility' : undefined}
        >
            <div
                className={`note__wrapper fp-eligibility-card${
                    hasEligibilityValues ? ` fp-eligibility-card--${overallStatus}` : ''
                }`}
            >
                <div className="fp-eligibility__chrome">
                    <header
                        className={`fp-eligibility-hero${
                            hasEligibilityValues ? ` fp-eligibility-hero--${overallStatus}` : ''
                        }`}
                    >
                        <div className="fp-eligibility-hero__top">
                            <span className="fp-eligibility-hero__eyebrow">
                                <BankIcon size={15} weight="fill" />
                                Eligibility scorecard
                            </span>
                            <Button type="text" size="small" className="note__expand" onClick={handleExpand}>
                                <MdClose />
                            </Button>
                        </div>
                        <div className="fp-eligibility-hero__main">
                            <div className="fp-eligibility-hero__identity">
                                <span className="fp-eligibility-hero__monogram" aria-hidden>
                                    {partnerInitials}
                                </span>
                                <div className="fp-eligibility-hero__copy">
                                    <span className="fp-eligibility-hero__partner">{partnerName}</span>
                                    <span className="fp-eligibility-hero__meta">Finance partner on this opportunity</span>
                                    {showSuggested ? (
                                        <span className="fp-eligibility-hero__suggest">
                                            Stronger fit: {suggestedName}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            <div className="fp-eligibility-hero__meter" aria-hidden={!hasEligibilityValues}>
                                <svg className="fp-eligibility-hero__ring" viewBox="0 0 48 48" width="56" height="56">
                                    <circle className="fp-eligibility-hero__ring-track" cx="24" cy="24" r={ringRadius} />
                                    <circle
                                        className="fp-eligibility-hero__ring-value"
                                        cx="24"
                                        cy="24"
                                        r={ringRadius}
                                        strokeDasharray={ringCircumference}
                                        strokeDashoffset={ringOffset}
                                    />
                                </svg>
                                <div className="fp-eligibility-hero__meter-copy">
                                    <strong>
                                        {hasEligibilityValues ? `${passedCount}/${criteriaTotal}` : '—'}
                                    </strong>
                                    <span>checks passed</span>
                                </div>
                            </div>
                        </div>
                        <div className="fp-eligibility-hero__actions">
                            {hasEligibilityValues ? (
                                <StatusIcon
                                    status={overallStatus}
                                    partner={partner}
                                    values={currentValues}
                                    reasonRows={overallReasonRows}
                                    label={overallLabel}
                                />
                            ) : (
                                <span className="fp-eligibility__value">No criteria</span>
                            )}
                            <Button
                                size="small"
                                className="fp-eligibility__change-btn"
                                icon={<ArrowsLeftRightIcon size={13} weight="bold" />}
                                onClick={() => setChangeOpen(true)}
                            >
                                Compare Partners
                            </Button>
                        </div>
                    </header>
                </div>
                <div className="fp-eligibility__body">
                    <ol className="fp-eligibility-scorecard">
                        {ELIGIBILITY_ROWS.map((row, index) => {
                            const serial = String(index + 1).padStart(2, '0');
                            const nestedRows =
                                row.key === 'propertyType'
                                    ? PROPERTY_TYPE_ROWS
                                    : row.key === 'installationType'
                                      ? INSTALLATION_TYPE_ROWS
                                      : row.key === 'systemSpecs'
                                        ? SYSTEM_SPEC_ROWS
                                        : null;
                            const rowStatus = statusByKey[row.key] ?? 'warning';
                            if (nestedRows) {
                                const nestedOpen =
                                    row.key === 'propertyType'
                                        ? propertyTypeOpen
                                        : row.key === 'installationType'
                                          ? installationTypeOpen
                                          : systemSpecsOpen;
                                const toggleNested =
                                    row.key === 'propertyType'
                                        ? () => setPropertyTypeOpen((open) => !open)
                                        : row.key === 'installationType'
                                          ? () => setInstallationTypeOpen((open) => !open)
                                          : () => setSystemSpecsOpen((open) => !open);
                                return (
                                    <li
                                        key={row.key}
                                        className={`fp-eligibility-check fp-eligibility-check--${rowStatus}${
                                            nestedOpen ? ' fp-eligibility-check--open' : ''
                                        }`}
                                    >
                                        <div
                                            className="fp-eligibility-check__row fp-eligibility-check__row--expandable"
                                            role="button"
                                            tabIndex={0}
                                            aria-expanded={nestedOpen}
                                            onClick={toggleNested}
                                            onKeyDown={(event) => {
                                                if (event.key !== 'Enter' && event.key !== ' ') return;
                                                event.preventDefault();
                                                toggleNested();
                                            }}
                                        >
                                            <span className="fp-eligibility-check__index">{serial}</span>
                                            <span className="fp-eligibility-check__copy">
                                                <span className="fp-eligibility-check__name">
                                                    {row.label.replace(/ Eligibility$/, '')}
                                                    <EligibilityInfoButton
                                                        label={row.label}
                                                        rowKey={row.key}
                                                        partner={partner}
                                                        values={currentValues}
                                                    />
                                                </span>
                                                <span className="fp-eligibility-check__value">
                                                    {formatEligibilityCurrentValue(row.key, currentValues)}
                                                </span>
                                            </span>
                                            <span className="fp-eligibility-check__end">
                                                {hasEligibilityValues ? (
                                                    <StatusIcon
                                                        status={rowStatus}
                                                        rowKey={row.key}
                                                        partner={partner}
                                                        values={currentValues}
                                                    />
                                                ) : (
                                                    '—'
                                                )}
                                                {nestedOpen ? (
                                                    <CaretDownIcon size={14} weight="bold" />
                                                ) : (
                                                    <CaretRightIcon size={14} weight="bold" />
                                                )}
                                            </span>
                                        </div>
                                        {nestedOpen ? (
                                            <ul className="fp-eligibility-check__children">
                                                {nestedRows.map((nested) => {
                                                    const childStatus = statusByKey[nested.key] ?? 'warning';
                                                    const isCurrent = isCurrentNestedEligibilityRow(
                                                        row.key,
                                                        nested.key,
                                                        currentValues,
                                                    );
                                                    return (
                                                        <li
                                                            key={nested.key}
                                                            className={`fp-eligibility-check__child${
                                                                isCurrent
                                                                    ? ' fp-eligibility-check__child--current'
                                                                    : ''
                                                            }`}
                                                        >
                                                            <span className="fp-eligibility-check__copy">
                                                                <span className="fp-eligibility-check__name">
                                                                    {nested.label}
                                                                    <EligibilityInfoButton
                                                                        label={nested.label}
                                                                        rowKey={nested.key}
                                                                        partner={partner}
                                                                        values={currentValues}
                                                                    />
                                                                </span>
                                                                <span className="fp-eligibility-check__value">
                                                                    {formatEligibilityCurrentValue(
                                                                        nested.key,
                                                                        currentValues,
                                                                    )}
                                                                </span>
                                                            </span>
                                                            {hasEligibilityValues ? (
                                                                <StatusIcon
                                                                    status={childStatus}
                                                                    rowKey={nested.key}
                                                                    partner={partner}
                                                                    values={currentValues}
                                                                />
                                                            ) : (
                                                                '—'
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        ) : null}
                                    </li>
                                );
                            }

                            return (
                                <li
                                    key={row.key}
                                    className={`fp-eligibility-check fp-eligibility-check--${rowStatus}`}
                                >
                                    <div className="fp-eligibility-check__row">
                                        <span className="fp-eligibility-check__index">{serial}</span>
                                        <span className="fp-eligibility-check__copy">
                                            <span className="fp-eligibility-check__name">
                                                {row.label.replace(/ Eligibility$/, '')}
                                                <EligibilityInfoButton
                                                    label={row.label}
                                                    rowKey={row.key}
                                                    partner={partner}
                                                    values={currentValues}
                                                />
                                            </span>
                                            <span className="fp-eligibility-check__value">
                                                {formatEligibilityCurrentValue(row.key, currentValues)}
                                            </span>
                                        </span>
                                        <span className="fp-eligibility-check__end">
                                            {hasEligibilityValues ? (
                                                <StatusIcon
                                                    status={rowStatus}
                                                    rowKey={row.key}
                                                    partner={partner}
                                                    values={currentValues}
                                                />
                                            ) : (
                                                '—'
                                            )}
                                        </span>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            </div>
            {!hideFloatingToggle && !asModal && hasCapability('Show_OneButton') ? (
            <Button
                title="Finance Partner Eligibility"
                type="default"
                icon={<BankIcon size={19} weight="fill" />}
                className={`note__toggle ${expand && 'show'}`}
                onClick={handleExpand}
            />
            ) : null}
        </div>
    );

    const detailsNode = details ? (
        <EligibilityDetailsModal details={details} onClose={() => setDetails(null)} />
    ) : null;

    const panelNode = (
        <EligibilityDetailsContext.Provider value={setDetails}>
            {asModal ? (
                <>
                    <button
                        type="button"
                        className="fp-eligibility-modal__backdrop"
                        aria-label="Close finance partner eligibility"
                        onClick={() => setExpand(false)}
                    />
                    {noteNode}
                </>
            ) : (
                noteNode
            )}
            {detailsNode}
        </EligibilityDetailsContext.Provider>
    );

    const changeModal = (
        <FinancePartnerChangeModal
            open={changeOpen}
            onClose={() => setChangeOpen(false)}
            catalog={eligibilityCatalog}
            currentValues={currentValues}
            currentPartnerName={partnerName}
        />
    );

    if (hideFloatingToggle && !expand) {
        return changeModal;
    }

    if (!portalTarget) {
        return (
            <>
                {panelNode}
                {changeModal}
            </>
        );
    }

    return (
        <>
            {createPortal(panelNode, portalTarget)}
            {changeModal}
        </>
    );
};

export default FinancePartnerEligibility;
